"use client";

import * as React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { type Editor } from "@tiptap/react";

// --- Icons ---
import { faChevronDown } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
// --- UI Primitives ---
import {
  UnifiedButton,
  UnifiedButtonProps,
} from "@/components/tiptap-ui/unified-button";
// --- Tiptap UI ---
import {
  TextAlignButton,
  textAlignLabels,
  useTextAlign,
  type TextAlign,
} from "@/components/tiptap-ui/text-align-button/text-align-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";

interface ListDropdownAlignProps extends Omit<UnifiedButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The alignments to display in the dropdown.
   */
  aligns?: TextAlign[];
  /**
   * Whether the dropdown should be hidden when no alignments are available
   * @default false
   */
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const ALIGN_OPTIONS: TextAlign[] = ["left", "center", "right", "justify"];

// Removed useDropdownAlignState hook entirely

export function AlignDropdownMenu({
  editor: providedEditor,
  aligns = ALIGN_OPTIONS,
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: ListDropdownAlignProps) {
  const editor = useTiptapEditor(providedEditor);
  const [isOpen, setIsOpen] = React.useState(false);

  // Call useTextAlign for each align, always in the same order
  const alignStates = aligns.map((align) => ({
    align,
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ...useTextAlign(editor, align),
  }));

  const alignAvailable = alignStates.some((s) => s.alignAvailable);
  const activeAlign = alignStates.find((s) => s.isActive)?.align;

  const handleOpenChange = React.useCallback(
    (open: boolean, callback?: (isOpen: boolean) => void) => {
      setIsOpen(open);
      callback?.(open);
    },
    [],
  );

  // Only show if at least one align option is available
  const show = React.useMemo(() => {
    if (!alignAvailable) return false;
    if (!editor?.isEditable) return false;
    return true;
  }, [alignAvailable, editor]);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => handleOpenChange(open, onOpenChange),
    [handleOpenChange, onOpenChange],
  );

  function getActiveAlignIcon(activeAlign: TextAlign | undefined) {
    const icon = activeAlign
      ? {
          left: faAlignLeft,
          center: faAlignCenter,
          right: faAlignRight,
          justify: faAlignJustify,
        }[activeAlign]
      : faAlignLeft;
    return <FontAwesomeIcon icon={icon} />;
  }

  if (!show) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <UnifiedButton
          type="button"
          data-style="ghost"
          data-active-state={activeAlign ? "on" : "off"}
          role="button"
          tabIndex={-1}
          aria-label="Text alignment options"
          tooltip="Align"
          {...props}
        >
          {getActiveAlignIcon(activeAlign)}
          <FontAwesomeIcon
            icon={faChevronDown}
            className="tiptap-button-dropdown-small"
          />
        </UnifiedButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuGroup>
          {aligns.map((align) => (
            <DropdownMenuItem key={align} asChild>
              <TextAlignButton
                editor={editor}
                align={align}
                text={textAlignLabels[align]}
                hideWhenUnavailable={hideWhenUnavailable}
                tooltip={""}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
